import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
    deleteCurrentSiteLogo,
    fetchCurrentSiteSettings,
    updateCurrentSiteSettings,
    uploadCurrentSiteLogo,
} from '../services/api';
import {
    getCurrentSiteConfig,
    getCurrentSiteKey,
    notifySiteBrandingUpdated,
    SiteKey,
    subscribeToSiteChange,
} from '../services/site';

const AdminSiteSettings: React.FC = () => {
    const history = useHistory();
    const [siteKey, setSiteKey] = useState<SiteKey>(getCurrentSiteKey());
    const [name, setName] = useState('');
    const [tagline, setTagline] = useState('');
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [primaryColor, setPrimaryColor] = useState('');
    const [backgroundColor, setBackgroundColor] = useState('');
    const [textColor, setTextColor] = useState('');
    const [logoMaxHeight, setLogoMaxHeight] = useState('');
    const [borderRadius, setBorderRadius] = useState('');
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [removingLogo, setRemovingLogo] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [status, setStatus] = useState<string | null>(null);

    const getApiErrorMessage = (fallback: string, error: any) => {
        const statusCode = error && error.response && error.response.status;
        const message = error && error.response && error.response.data && error.response.data.message;
        if (statusCode === 401 || statusCode === 403) {
            window.setTimeout(() => {
                history.push('/login');
            }, 100);
            return 'Din session er udloeber. Log ind igen for at gemme aendringer.';
        }
        return message || fallback;
    };

    const loadSettings = useCallback(async () => {
        const fallbackSite = getCurrentSiteConfig();
        setLoading(true);
        setError(null);

        try {
            const data = await fetchCurrentSiteSettings(siteKey);
            setName((data?.name || fallbackSite.name || '').trim());
            setTagline((data?.tagline || fallbackSite.tagline || '').trim());
            setLogoUrl(data?.logoUrl || null);
            setPrimaryColor(data?.theme?.primaryColor || '');
            setBackgroundColor(data?.theme?.backgroundColor || '');
            setTextColor(data?.theme?.textColor || '');
            setLogoMaxHeight(data?.theme?.logoMaxHeight !== undefined ? String(data.theme.logoMaxHeight) : '');
            setBorderRadius(data?.theme?.borderRadius !== undefined ? String(data.theme.borderRadius) : '');
        } catch (loadError) {
            setName(fallbackSite.name || '');
            setTagline(fallbackSite.tagline || '');
            setLogoUrl(null);
            setPrimaryColor('');
            setBackgroundColor('');
            setTextColor('');
            setLogoMaxHeight('');
            setBorderRadius('');
            setError('Kunne ikke hente site indstillinger. Viser fallback-vaerdier.');
        } finally {
            setLoading(false);
        }
    }, [siteKey]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    useEffect(() => subscribeToSiteChange((nextSiteKey) => {
        setSiteKey(nextSiteKey);
        setStatus(null);
        setError(null);
        setLogoFile(null);
    }), []);

    const handleSave = async (event: React.FormEvent) => {
        event.preventDefault();
        setSaving(true);
        setError(null);
        setStatus(null);

        try {
            const updated = await updateCurrentSiteSettings({
                name: name.trim(),
                tagline: tagline.trim() || null,
                theme: {
                    primaryColor: primaryColor.trim() || null,
                    backgroundColor: backgroundColor.trim() || null,
                    textColor: textColor.trim() || null,
                    logoMaxHeight: logoMaxHeight.trim() || null,
                    borderRadius: borderRadius.trim() || null,
                },
            }, siteKey);
            setName((updated?.name || '').trim());
            setTagline((updated?.tagline || '').trim());
            setPrimaryColor(updated?.theme?.primaryColor || '');
            setBackgroundColor(updated?.theme?.backgroundColor || '');
            setTextColor(updated?.theme?.textColor || '');
            setLogoMaxHeight(updated?.theme?.logoMaxHeight !== undefined ? String(updated.theme.logoMaxHeight) : '');
            setBorderRadius(updated?.theme?.borderRadius !== undefined ? String(updated.theme.borderRadius) : '');
            setStatus('Site indstillinger blev gemt.');
            notifySiteBrandingUpdated(siteKey);
            await loadSettings();
        } catch (saveError) {
            setError(getApiErrorMessage('Kunne ikke gemme site indstillinger.', saveError));
        } finally {
            setSaving(false);
        }
    };

    const handleUploadLogo = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!logoFile) {
            setError('Vaelg et logo foerst.');
            return;
        }

        setUploading(true);
        setError(null);
        setStatus(null);

        try {
            const updated = await uploadCurrentSiteLogo(logoFile, siteKey);
            setLogoUrl(updated?.logoUrl || null);
            setLogoFile(null);
            setStatus(`Logo blev uploadet for site: ${siteKey}.`);
            notifySiteBrandingUpdated(siteKey);
            await loadSettings();
        } catch (uploadError) {
            setError(getApiErrorMessage('Kunne ikke uploade logo.', uploadError));
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteLogo = async () => {
        if (!logoUrl) return;
        if (!window.confirm('Vil du fjerne logoet for dette site?')) return;

        setRemovingLogo(true);
        setError(null);
        setStatus(null);

        try {
            const updated = await deleteCurrentSiteLogo(siteKey);
            if (updated && updated.logoUrl) {
                setError('Backend returnerede stadig et logo. Proev igen eller genindlaes siden.');
                return;
            }

            setLogoUrl(null);
            setLogoFile(null);
            setStatus(`Logo blev fjernet for site: ${siteKey}.`);
            notifySiteBrandingUpdated(siteKey);
            await loadSettings();
        } catch (deleteError) {
            setError(getApiErrorMessage('Kunne ikke fjerne logo.', deleteError));
        } finally {
            setRemovingLogo(false);
        }
    };

    const isHexColor = (value: string) => /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test((value || '').trim());
    const previewPrimaryColor = isHexColor(primaryColor) ? primaryColor.trim() : '#1f1f1f';
    const previewBackgroundColor = isHexColor(backgroundColor) ? backgroundColor.trim() : '#f5f3ee';
    const previewTextColor = isHexColor(textColor) ? textColor.trim() : '#1f1f1f';
    const previewLogoSrc = logoUrl ? encodeURI(logoUrl) : '';

    const handleColorChange = (
        setter: React.Dispatch<React.SetStateAction<string>>,
        nextValue: string
    ) => {
        setter(nextValue || '');
    };
    const parsedLogoMaxHeight = Number(logoMaxHeight);
    const previewLogoMaxHeight = Number.isFinite(parsedLogoMaxHeight)
        ? Math.max(28, Math.min(160, Math.round(parsedLogoMaxHeight)))
        : 68;
    const parsedBorderRadius = Number(borderRadius);
    const previewBorderRadius = Number.isFinite(parsedBorderRadius)
        ? Math.max(0, Math.min(32, Math.round(parsedBorderRadius)))
        : 12;

    return (
        <div className="admin-site-settings">
            <div className="admin-image-edit__header">
                <div>
                    <h1>Site indstillinger</h1>
                    <p className="gallery-list-meta">Aktivt site: {siteKey}</p>
                </div>
                <div className="admin-actions">
                    <Link className="link-button" to="/admin">
                        Tilbage til admin
                    </Link>
                </div>
            </div>

            {loading ? (
                <p>Indlaeser site indstillinger...</p>
            ) : (
                <>
                    {error && <div className="error">{error}</div>}
                    {status && <div className="gallery-list-meta">{status}</div>}

                    <section className="admin-site-settings__section">
                        <h2>Branding tekst</h2>
                        <form className="admin-site-settings__form" onSubmit={handleSave}>
                            <label>
                                Title
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(event) => setName(event.target.value)}
                                    required
                                />
                            </label>
                            <label>
                                Undertitle
                                <input
                                    type="text"
                                    value={tagline}
                                    onChange={(event) => setTagline(event.target.value)}
                                />
                            </label>
                            <h3>Theme</h3>
                            <p className="gallery-list-meta">Hex farver (#rrggbb) og numeriske vaerdier for layout.</p>
                            <label>
                                Primaer farve
                                <span className="admin-site-settings__color-row">
                                    <input
                                        type="color"
                                        value={previewPrimaryColor}
                                        onChange={(event) => handleColorChange(setPrimaryColor, event.target.value)}
                                        aria-label="Vaelg primaer farve"
                                    />
                                    <input
                                        type="text"
                                        placeholder="#1f1f1f"
                                        value={primaryColor}
                                        onChange={(event) => setPrimaryColor(event.target.value)}
                                    />
                                </span>
                            </label>
                            <label>
                                Baggrundsfarve
                                <span className="admin-site-settings__color-row">
                                    <input
                                        type="color"
                                        value={previewBackgroundColor}
                                        onChange={(event) => handleColorChange(setBackgroundColor, event.target.value)}
                                        aria-label="Vaelg baggrundsfarve"
                                    />
                                    <input
                                        type="text"
                                        placeholder="#f5f3ee"
                                        value={backgroundColor}
                                        onChange={(event) => setBackgroundColor(event.target.value)}
                                    />
                                </span>
                            </label>
                            <label>
                                Tekstfarve
                                <span className="admin-site-settings__color-row">
                                    <input
                                        type="color"
                                        value={previewTextColor}
                                        onChange={(event) => handleColorChange(setTextColor, event.target.value)}
                                        aria-label="Vaelg tekstfarve"
                                    />
                                    <input
                                        type="text"
                                        placeholder="#1f1f1f"
                                        value={textColor}
                                        onChange={(event) => setTextColor(event.target.value)}
                                    />
                                </span>
                            </label>
                            <label>
                                Logo max hoejde (px)
                                <input
                                    type="number"
                                    min={28}
                                    max={160}
                                    value={logoMaxHeight}
                                    onChange={(event) => setLogoMaxHeight(event.target.value)}
                                />
                            </label>
                            <label>
                                Border radius (px)
                                <input
                                    type="number"
                                    min={0}
                                    max={32}
                                    value={borderRadius}
                                    onChange={(event) => setBorderRadius(event.target.value)}
                                />
                            </label>

                            <div className="admin-site-settings__theme-preview">
                                <div className="admin-site-settings__swatches">
                                    <div className="admin-site-settings__swatch-item">
                                        <span className="admin-site-settings__swatch-label">Primaer</span>
                                        <span className="admin-site-settings__swatch" style={{ backgroundColor: previewPrimaryColor }} />
                                        <span className="gallery-list-meta">{previewPrimaryColor}</span>
                                    </div>
                                    <div className="admin-site-settings__swatch-item">
                                        <span className="admin-site-settings__swatch-label">Baggrund</span>
                                        <span className="admin-site-settings__swatch" style={{ backgroundColor: previewBackgroundColor }} />
                                        <span className="gallery-list-meta">{previewBackgroundColor}</span>
                                    </div>
                                    <div className="admin-site-settings__swatch-item">
                                        <span className="admin-site-settings__swatch-label">Tekst</span>
                                        <span className="admin-site-settings__swatch" style={{ backgroundColor: previewTextColor }} />
                                        <span className="gallery-list-meta">{previewTextColor}</span>
                                    </div>
                                </div>

                                <div
                                    className="admin-site-settings__mini-preview"
                                    style={{
                                        backgroundColor: previewBackgroundColor,
                                        color: previewTextColor,
                                        borderRadius: `${previewBorderRadius}px`,
                                    }}
                                >
                                    <div className="admin-site-settings__mini-preview-header">
                                        <div
                                            className="admin-site-settings__mini-logo"
                                            style={{
                                                maxHeight: `${previewLogoMaxHeight}px`,
                                                borderColor: previewPrimaryColor,
                                            }}
                                        >
                                            {previewLogoSrc ? (
                                                <img
                                                    src={previewLogoSrc}
                                                    alt={name || 'Preview logo'}
                                                    className="admin-site-settings__mini-logo-image"
                                                />
                                            ) : (
                                                'Logo'
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="admin-site-settings__mini-button"
                                            style={{ backgroundColor: previewPrimaryColor }}
                                        >
                                            Accent
                                        </button>
                                    </div>
                                    <strong>{name || 'Site title'}</strong>
                                    <p>{tagline || 'Undertitle preview'}</p>
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="submit" disabled={saving}>
                                    {saving ? 'Gemmer...' : 'Gem indstillinger'}
                                </button>
                            </div>
                        </form>
                    </section>

                    <section className="admin-site-settings__section">
                        <h2>Logo</h2>
                        <p className="gallery-list-meta">Fjerner/uploader logo for site: {siteKey}</p>
                        {logoUrl ? (
                            <div className="admin-site-settings__logo-preview-wrap">
                                <img
                                    src={logoUrl}
                                    alt={name || 'Site logo'}
                                    className="admin-site-settings__logo-preview"
                                />
                            </div>
                        ) : (
                            <p className="gallery-list-meta">Intet logo sat. Navigationen bruger tekst-fallback.</p>
                        )}

                        <form className="admin-site-settings__logo-form" onSubmit={handleUploadLogo}>
                            <label>
                                Upload nyt logo
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(event) => setLogoFile(event.target.files?.[0] || null)}
                                />
                            </label>
                            {logoFile ? (
                                <p className="gallery-list-meta">Valgt fil: {logoFile.name}</p>
                            ) : null}
                            <div className="modal-actions">
                                <button type="submit" disabled={uploading || !logoFile}>
                                    {uploading ? 'Uploader...' : 'Upload logo'}
                                </button>
                                <button
                                    type="button"
                                    className="link-button link-button--danger"
                                    onClick={handleDeleteLogo}
                                    disabled={removingLogo || !logoUrl}
                                >
                                    {removingLogo ? 'Fjerner...' : 'Fjern logo'}
                                </button>
                            </div>
                        </form>
                    </section>
                </>
            )}
        </div>
    );
};

export default AdminSiteSettings;
